// @flow
import * as React from 'react';

import { File as FileIcon, X } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { FileUpload, Text, View } from 'reshaped';

import type { ChangeHandler } from 'reshaped';

import { Icon, Button, Link } from '@gandi/reshaped';
import { useTranslator, mapTags } from '@gandi/translate';
import { ProductDomain } from '@gandi/ui/icons';

import { useDragAndDrop } from 'src/Domain/BulkInput/useDragAndDrop';

type Props = {
  field: {
    onChange: (value: any) => void,
    onBlur: () => void,
    value: mixed,
    disabled: boolean,
    name: string,
    ref: React.RefObject<HTMLInputElement>,
  },
};

// Accept both extensions and MIME types as requested by the ticket
const accept =
  '.csv, .tab, .tsv, .txt, ' +
  'text/csv, text/tab-separated-values, text/plain';

export function FileUploadField({ field }: Props): React.MixedElement {
  const { __ } = useTranslator();
  const form = useFormContext();

  const [fileName, setFileName] = React.useState<string>('');

  const { handleFile } = useDragAndDrop({
    setDomains: field.onChange,
    setFileName,
    setError: message => form.setError('domains', { message }),
  });

  const handleFileChange: ChangeHandler<Array<File>, DragEvent> = ({
    value,
  }) => {
    const file = value?.[0];
    if (file) {
      // $FlowFixMe[incompatible-call]
      handleFile(file);
    }
  };

  function handleRemove() {
    form.resetField('domains');
    setFileName('');
  }

  return fileName ? (
    <View
      gap={2}
      paddingInline={4}
      paddingBlock={2}
      align="center"
      direction="row"
      borderColor="primary-faded"
      borderRadius="medium"
      wrap={false}
      width="fit-content"
    >
      <Icon svg={ProductDomain} color="product-domain" size={5} />
      <Text variant="body-2" maxLines={1}>
        {fileName}
      </Text>

      <View.Item gapBefore={5}>
        <Button
          onClick={handleRemove}
          variant="ghost"
          icon={X}
          attributes={{ 'aria-label': __('Remove the file') }}
        />
      </View.Item>
    </View>
  ) : (
    // $FlowFixMe[prop-missing]
    <FileUpload
      name="file"
      height={36}
      onChange={handleFileChange}
      inputAttributes={{
        multiple: false,
        accept,
        'aria-label': __('Upload file containing the list of domains'),
      }}
    >
      <View direction="row" gap={2} align="center">
        <Icon svg={FileIcon} size={4} />
        <Text variant="body-3">
          {mapTags(
            __('Drop files to attach, or [[link: browse]]'),
            ({ link }) => ({
              link: (
                <FileUpload.Trigger>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link>{link}</Link>
                </FileUpload.Trigger>
              ),
            }),
          )}
        </Text>
      </View>
    </FileUpload>
  );
}
